'use client';

import { Button, Flex, Tabs, TabsValueChangeDetails, Text } from '@chakra-ui/react';
import { memo, useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import { LuPlus } from 'react-icons/lu';

import PaymentLinksTable from '@features/finance/PaymentLinksTable';
import { allPaymentRequests, PaymentRequest } from 'helpers/fakeData';
import { useHref } from 'hooks/useHref';

type ActiveTab = 'all' | 'open' | 'fulfilled' | 'closed';

const tabs: Array<{ label: string; value: ActiveTab }> = [
  { label: 'All', value: 'all' },
  { label: 'Open', value: 'open' },
  { label: 'Fulfilled', value: 'fulfilled' },
  { label: 'Closed', value: 'closed' },
];

const matchesTab = (request: PaymentRequest, tab: ActiveTab): boolean => {
  switch (tab) {
    case 'open':
      return request.status === 'open' || request.status === 'partially_paid';
    case 'fulfilled':
      return request.status === 'fulfilled';
    case 'closed':
      return request.status === 'closed' || request.status === 'cancelled';
    case 'all':
    default:
      return true;
  }
};

const PaymentLinksPage = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('all');
  const router = useRouter();
  const { paymentLinkBuilderHref } = useHref();

  const handleTabChange = useCallback((details: TabsValueChangeDetails) => {
    setActiveTab(details.value as ActiveTab);
  }, []);

  const onClickNew = useCallback(() => {
    router.push(paymentLinkBuilderHref());
  }, [router, paymentLinkBuilderHref]);

  return (
    <Flex flexDirection="column" gap={4} p={4}>
      <Flex justifyContent="space-between" alignItems="center">
        <Flex direction="column" gap={0.5}>
          <Text fontSize="lg" fontWeight="bold">
            Payment links
          </Text>
          <Text fontSize="sm" color="text.secondary">
            Collect from your clients in local currency. Pay your suppliers in USDC.
          </Text>
        </Flex>
        <Button size="2xs" borderRadius="md" onClick={onClickNew}>
          <LuPlus size={14} />
          New payment link
        </Button>
      </Flex>
      <Tabs.Root
        value={activeTab}
        variant="line"
        size="md"
        onValueChange={handleTabChange}
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <Tabs.List borderColor="border.light" alignItems="center">
          {tabs.map((tab) => (
            <Tabs.Trigger key={tab.value} value={tab.value}>
              <Text fontSize="sm" fontWeight="medium">
                {tab.label}
              </Text>
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {tabs.map((tab) => (
          <Tabs.Content key={tab.value} value={tab.value} p={0}>
            <PaymentLinksTable
              requests={allPaymentRequests.filter((request) => matchesTab(request, tab.value))}
            />
          </Tabs.Content>
        ))}
      </Tabs.Root>
    </Flex>
  );
};

export default memo(PaymentLinksPage);
